import { useState } from "react";
import { Moon, Sun } from "lucide-react";
import { cn } from "@/lib/cn";
import { useUiStore, type WallpaperId } from "@/store";
import {
  SettingsCard,
  SettingsNote,
  SettingsPanel,
  SettingsRow,
  SettingsSelectRow,
  SettingsToggle,
} from "../SettingsUI";

const wallpapers: Array<{ id: WallpaperId; label: string; swatch: string }> = [
  { id: "default", label: "Default", swatch: "bg-surface-muted" },
  { id: "papaya", label: "Papaya", swatch: "bg-papaya/30" },
  { id: "leaf", label: "Leaf", swatch: "bg-papaya-leaf/30" },
  { id: "dusk", label: "Dusk", swatch: "bg-papaya-deep" },
];

export function ChatsSection() {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const wallpaper = useUiStore((s) => s.wallpaper);
  const setWallpaper = useUiStore((s) => s.setWallpaper);
  const [enterIsSend, setEnterIsSend] = useState(true);
  const [mediaVisibility, setMediaVisibility] = useState(true);
  const [fontSize, setFontSize] = useState("medium");
  const [keepChatsArchived, setKeepChatsArchived] = useState(false);

  return (
    <SettingsPanel title="Chats" description="Theme, wallpaper, chat settings">
      <SettingsCard title="Display">
        <div className="px-4 py-3.5">
          <p className="mb-3 text-sm font-medium text-fg">Theme</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={cn(
                "flex items-center justify-center gap-2 rounded-[6px] border px-3 py-3 text-sm font-medium",
                theme === "light"
                  ? "border-papaya-deep bg-papaya/15 text-fg"
                  : "border-border text-fg-muted hover:bg-surface-muted",
              )}
            >
              <Sun className="h-4 w-4" />
              Light
            </button>
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={cn(
                "flex items-center justify-center gap-2 rounded-[6px] border px-3 py-3 text-sm font-medium",
                theme === "dark"
                  ? "border-papaya-deep bg-papaya/15 text-fg"
                  : "border-border text-fg-muted hover:bg-surface-muted",
              )}
            >
              <Moon className="h-4 w-4" />
              Dark
            </button>
          </div>
        </div>

        <SettingsSelectRow
          title="Font size"
          value={fontSize}
          onChange={setFontSize}
          options={[
            { value: "small", label: "Small" },
            { value: "medium", label: "Medium" },
            { value: "large", label: "Large" },
          ]}
        />
      </SettingsCard>

      <SettingsCard title="Chat wallpaper">
        <div className="grid grid-cols-2 gap-2 px-4 py-3.5 sm:grid-cols-4">
          {wallpapers.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setWallpaper(item.id)}
              className={cn(
                "overflow-hidden rounded-[6px] border text-left",
                wallpaper === item.id
                  ? "border-papaya-deep ring-2 ring-papaya/30"
                  : "border-border hover:border-papaya/40",
              )}
            >
              <div className={cn("h-16 w-full", item.swatch)} />
              <p className="px-2 py-1.5 text-xs font-medium text-fg">{item.label}</p>
            </button>
          ))}
        </div>
      </SettingsCard>

      <SettingsCard title="Chat settings">
        <SettingsToggle
          title="Enter is send"
          description="Press Enter to send a message; Shift+Enter for a new line"
          checked={enterIsSend}
          onChange={setEnterIsSend}
        />
        <SettingsToggle
          title="Media visibility"
          description="Show newly downloaded media in your device gallery"
          checked={mediaVisibility}
          onChange={setMediaVisibility}
        />
        <SettingsToggle
          title="Keep chats archived"
          description="Archived chats stay archived when you receive a new message"
          checked={keepChatsArchived}
          onChange={setKeepChatsArchived}
        />
        <SettingsRow title="Chat backup" description="Manage backup frequency and include videos" />
        <SettingsRow title="Clear all chats" description="Delete messages from all conversations" danger />
      </SettingsCard>

      <SettingsNote>
        Theme and wallpaper apply on Home right away. Other chat options are preview controls for the web UI.
      </SettingsNote>
    </SettingsPanel>
  );
}
